<template>
  <div class="control-basic-information">
    <h6>Basic Details</h6>

    <div class="form-group">
      <label>Unique ID</label>
      <input type="text" class="form-control" :value="control.uniqueId" readonly>
    </div>

    <div class="form-group">
      <label>Name (Must be unique)</label>
      <input type="text" class="form-control" v-model="control.name">
    </div>

    <div class="form-group">
      <label>Label</label>
      <input type="text" class="form-control" v-model="control.label">
    </div>

    <div class="form-group">
      <label>Sub-label</label>
      <input type="text" class="form-control" v-model="control.subLabel">
    </div>

    <div class="form-group checkbox-group">
      <label>
        <input type="checkbox" v-model="control.isShowLabel">
        Show Label?
      </label>
    </div>

    <!-- Pre-fill Patient Name checkbox - only for OTHER form type and input fields -->
    <div class="form-group checkbox-group" v-if="showPrefillPatientNameOption">
      <label>
        <input type="checkbox" v-model="control.prefillPatientName" @change="onPrefillPatientNameChange">
        Pre-fill Patient Name
      </label>
      <small class="prefill-hint">When enabled, this field will be pre-filled with the patient's name (Last, First) and will be read-only.</small>
    </div>

    <!-- Pre-fill with Teeth #'s checkbox - only for OTHER form type and input fields -->
    <div class="form-group checkbox-group" v-if="showPrefillTeethNumbersOption">
      <label>
        <input type="checkbox" v-model="control.prefillTeethNumbers" @change="onPrefillTeethNumbersChange">
        Pre-fill with Teeth #'s
      </label>
      <small class="prefill-hint">When enabled, this field will be pre-filled with tooth numbers from the patient's next scheduled appointment.</small>
    </div>

    <div class="form-group">
      <label>Field Placeholder (Optional)</label>
      <input
        type="text"
        class="form-control"
        v-model="control.placeholderText"
        :disabled="control.prefillPatientName || control.prefillTeethNumbers"
        :class="{ 'disabled-field': control.prefillPatientName || control.prefillTeethNumbers }"
      >
      <small v-if="control.prefillPatientName || control.prefillTeethNumbers" class="placeholder-disabled-hint">Placeholder is disabled when a pre-fill option is enabled.</small>
    </div>

    <div class="form-group">
      <label>Default Value (Optional)</label>
      <input type="text" class="form-control" v-model="control.defaultValue">
    </div>

    <div class="form-group" v-if="showODColumnOption">
      <label>Save to Open Dental Field (Optional)</label>
      <select class="form-control" v-model="control.teamioODColumn">
        <option value="">-- Select Field --</option>
        <option v-for="(item, index) in teamioFields"
                :key="index"
                :value="item.val">
                {{item.key}}
        </option>
      </select>
    </div>

    <!-- Stale mapping on a form that may no longer write to Open Dental. The dropdown is hidden,
         so surface the existing value rather than letting it keep writing invisibly. -->
    <div class="form-group od-stale-mapping" v-else-if="control.teamioODColumn">
      <label>Save to Open Dental Field</label>
      <input type="text" class="form-control" :value="odColumnLabel(control.teamioODColumn)" readonly>
      <small class="od-stale-hint">
        This field is still mapped to Open Dental and will keep writing on every submission.
        Consent and instruction forms should not update Open Dental.
      </small>
      <button type="button" class="btn btn-sm btn-danger od-clear-mapping" @click="clearODColumn">
        Clear mapping
      </button>
    </div>

  </div>
</template>

<script>
export default {
  name: 'ControlBasicInformation',
  props: {
    control: { type: Object, required: true },
    siteID: { type: String },
    formType: { type: String, default: null }
  },

  computed: {
    /**
     * Show the "Pre-fill Patient Name" checkbox only when:
     * 1. Form type is "OTHER"
     * 2. Control type is "input" (Input Field)
     */
    showPrefillPatientNameOption() {
      return this.formType === 'OTHER' && this.control && this.control.type === 'input';
    },
    showPrefillTeethNumbersOption() {
      return this.formType === 'OTHER' && this.control && this.control.type === 'input';
    },
    /**
     * Show the "Save to Open Dental Field" dropdown only for form types that are meant to
     * update the patient record. OTHER forms are consent/instruction forms and must never
     * write to Open Dental — a "Patient Name" control there is pre-filled with "Last, First",
     * so mapping it to FName corrupts the patient's first name in Open Dental.
     *
     * The truthiness check is deliberate: while creating a form the type isn't picked until
     * save, so an unknown type must hide the dropdown. Otherwise a control could be mapped
     * before the type is chosen and the mapping would survive, invisible, once OTHER is picked.
     */
    showODColumnOption() {
      return !!this.formType && this.formType !== 'OTHER';
    }
  },
  data() {
    return {
      apiURL: `${process.env.VUE_APP_TEAMIO_API_URL}/get-od-data`,
      teamioFields: [
        {val: 'FName', key: 'First Name'},
        {val: 'LName', key: 'Last Name'},
        {val: 'MiddleI', key: 'Middle Initial'},
        {val: 'Title', key: 'Title'},
        {val: 'Preferred', key: 'Preferred Name'},
        {val: 'Birthdate', key: 'Birth Date'},
        {val: 'Position', key: 'Position'},
        {val: 'Gender', key: 'Gender'},
        {val: 'HmPhone', key: 'Home Phone'},
        {val: 'WkPhone', key: 'Work Phone'},
        {val: 'WirelessPhone', key: 'Wireless Phone'},
        {val: 'Email', key: 'Email'},
        {val: 'Address', key: 'Address 1'},
        {val: 'Address2', key: 'Address 2'},
        {val: 'City', key: 'City'},
        {val: 'State', key: 'State'},
        {val: 'Zip', key: 'Zip'},
        {val: 'PreferContactMethod', key: 'Preferred Contact Method'},
        {val: 'SSN', key: 'Social Security Number'},
        {val: 'Premed', key: 'Premed'},
        {val: 'TxtMsgOk', key: 'Text-Message-Ok'},
        {val: 'MedUrgNote', key: 'Medical Urgent Notes'},
        {val: 'Medical', key: 'Medical Summary'},
        {val: 'Service', key: 'Service Notes'},
        {val: 'MedicalComp', key: 'Complete Medical History'},
        {val: 'ICEName', key: 'Emergency Contact Name'},
        {val: 'ICEPhone', key: 'Emergency Contact Phone'},
        {val: 'Allergies', key: 'Allergies'},
        {val: 'Other Allergies', key: 'Other Allergies'},
        {val: 'Diseases', key: 'Diseases'},
        {val: 'Other Problems', key: 'Other Problems'},
        {val: 'Medications', key: 'Medications'},
        {val: 'Other Medications', key: 'Other Medications'},
        {val: 'Referrals', key: 'Referrals'}
      ]
    }
  },

  created() {
    console.log('ControlBasicInformation: getting custom fields');
    this.getCustomFields();
  },

  methods: {
    odColumnLabel(val) {
      const match = this.teamioFields.find(f => f.val === val);
      return match ? match.key : val;
    },
    clearODColumn() {
      this.$set(this.control, 'teamioODColumn', '');
    },
    onPrefillPatientNameChange() {
      if (this.control.prefillPatientName) {
        this.$set(this.control, 'prefillTeethNumbers', false);
      }
    },
    onPrefillTeethNumbersChange() {
      if (this.control.prefillTeethNumbers) {
        this.$set(this.control, 'prefillPatientName', false);
      }
    },
    getCustomFields() {
      if (!this.siteID) {
        console.log('ControlBasicInformation: No siteID provided, skipping custom fields fetch');
        return;
      }

      let payload = {
        siteID: this.siteID,
        dataType: 'CUSTOM_FIELDS'
      }

      fetch(this.apiURL, {
        method: "POST",
        body: JSON.stringify(payload)
      }).then(result => result.json())
      .then(this.afterRestAPICallDataSuccessfully)
      .catch(this.restAPICallErrorHandling);
    },

    afterRestAPICallDataSuccessfully(result) {
      console.log('ControlBasicInformation: custom fields loaded', result);
      let _this = this;
      if (result && Array.isArray(result)) {
        result.forEach(element => {
          _this.teamioFields.push({
            key: element.FieldName,
            val: `od-custom-${element.FieldName}`,
          })
        });
      }
    },

    restAPICallErrorHandling(err) {
      console.log('ControlBasicInformation: error fetching custom fields', err);
    }
  }
}
</script>

<style scoped>
.control-basic-information h6 {
  margin-bottom: 12px;
  font-weight: 600;
  color: #374151;
}

.form-group {
  margin-bottom: 12px;
}

.form-group label {
  display: block;
  margin-bottom: 4px;
  font-size: 13px;
  font-weight: 500;
  color: #4b5563;
}

.form-group.checkbox-group label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.form-group.checkbox-group input[type="checkbox"] {
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.form-control {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.form-control:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-control[readonly] {
  background-color: #f3f4f6;
  color: #6b7280;
  cursor: not-allowed;
}

select.form-control {
  cursor: pointer;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 8px center;
  background-repeat: no-repeat;
  background-size: 20px;
  padding-right: 32px;
  appearance: none;
}

/* Pre-fill Patient Name option styles */
.prefill-hint {
  display: block;
  margin-top: 4px;
  margin-left: 24px;
  font-size: 11px;
  color: #6b7280;
  line-height: 1.4;
}

.disabled-field {
  background-color: #f3f4f6;
  color: #9ca3af;
  cursor: not-allowed;
}

.placeholder-disabled-hint {
  display: block;
  margin-top: 4px;
  font-size: 11px;
  color: #9ca3af;
  font-style: italic;
}

/* Stale Open Dental mapping on a form type that shouldn't have one */
.od-stale-mapping {
  padding: 10px;
  border: 1px solid #fca5a5;
  border-radius: 6px;
  background-color: #fef2f2;
}

.od-stale-hint {
  display: block;
  margin-top: 4px;
  font-size: 11px;
  color: #b91c1c;
  line-height: 1.4;
}

.od-clear-mapping {
  margin-top: 8px;
}
</style>
